<template>
  <div class="product-page">
    <!-- 顶部导航栏 -->
    <PageHeader />
    <PageBanner
        title="产品展示"
        subtitle="汇聚协会会员企业优质产品，覆盖生产制造、商贸流通、全场景物流与数字科技等领域。"
    />

    <!-- 筛选栏 -->
    <div class="wrap filter-bar">
      <div class="filter-inner">
        <div class="filter-group">
          <span class="label">产品分类</span>
          <span
              class="chip"
              :class="{ active: activeType === 0 }"
              @click="filterAll()"
          >全部</span>
          <span
              class="chip"
              v-for="type in lv1Types"
              :key="type.id"
              :class="{ active: activeType === type.id }"
              @click="filterLv1(type)"
          >{{ type.name }}</span>
        </div>
        <div class="filter-group" v-if="lv2Types.length">
          <span class="label">子分类</span>
          <span
              class="chip"
              :class="{ active: activeType === activeLv1 }"
              @click="filterLv1(currentLv1Type)"
          >全部</span>
          <span
              class="chip"
              v-for="child in lv2Types"
              :key="child.id"
              :class="{ active: activeType === child.id }"
              @click="filterLv2(child)"
          >{{ child.name }}</span>
        </div>
        <div class="search">
          <input
              type="text"
              v-model="queryParams.title"
              placeholder="输入产品名称 / 关键词搜索"
              @keyup.enter="goPage(1)"
          />
          <button @click="goPage(1)">搜索</button>
        </div>
      </div>
    </div>

    <!-- 产品名录 -->
    <section class="wrap">
      <div class="list-head">
        <div class="cn">产品展示</div>
        <div class="count">共 {{ total }} 件产品 · 每页 {{ queryParams.pageSize }} 件</div>
      </div>

      <div class="product-grid" v-if="products.length">
        <router-link
            v-for="product in products"
            :key="product.id"
            :to="{ name: 'ProductDetails', params: { id: product.id } }"
            class="product-card"
        >
          <div class="product-cover">
            <img :src="product.cover ? product.cover : '/images/defaultProductFace.png'" :alt="product.title"/>
          </div>
          <div class="product-body">
            <b>{{ product.title }}</b>
            <div class="product-meta">
              <span class="price">{{ product.guidePrice }}</span>
              <span class="link">了解详情 →</span>
            </div>
          </div>
        </router-link>
      </div>
      <div class="loading" v-if="isLoading">加载中...</div>
      <div class="no-data" v-if="!isLoading && !products.length">暂无产品数据</div>

      <!-- 分页 -->
      <div class="pager" v-if="totalPages > 1">
        <a @click="goPage(queryParams.pageNum - 1)" v-if="queryParams.pageNum > 1">«</a>
        <a
            v-for="p in pagerItems"
            :key="p"
            :class="{ cur: p === queryParams.pageNum }"
            @click="p !== '…' && goPage(p)"
        >{{ p }}</a>
        <a @click="goPage(queryParams.pageNum + 1)" v-if="queryParams.pageNum < totalPages">»</a>
      </div>
    </section>

    <FooterComponent/>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import PageHeader from "@/components/PageHeader.vue";
import FooterComponent from "@/components/FooterComponent.vue";
import PageBanner from "@/components/PageBanner.vue";
import { noPermissionList } from "@/api/product";
import { getTypeTree } from "@/api/productTypeApi";

// 产品列表数据
const products = ref([]);
const isLoading = ref(false);
const total = ref(0);

// 请求参数
const queryParams = ref({
  pageNum: 1,
  pageSize: 12,
  title: undefined,
  typeId: undefined,
});

// 分类树（根节点的 children 为一级分类）
const treeData = ref([]);
const activeType = ref(0);   // 当前选中的分类 id（0 = 全部）
const activeLv1 = ref(0);    // 当前选中的一级分类 id
const currentLv1Type = ref(null);

const lv1Types = computed(() => (treeData.value[0] && treeData.value[0].children) || []);
const lv2Types = computed(() => (currentLv1Type.value && currentLv1Type.value.children) || []);

const totalPages = computed(() => Math.max(1, Math.ceil(total.value / queryParams.value.pageSize)));

// 分页按钮：当前页前后各 1 页 + 首尾 + 省略号
const pagerItems = computed(() => {
  const t = totalPages.value;
  const cur = queryParams.value.pageNum;
  const items = [];
  if (t <= 7) {
    for (let i = 1; i <= t; i++) items.push(i);
    return items;
  }
  items.push(1);
  if (cur > 3) items.push('…');
  for (let i = Math.max(2, cur - 1); i <= Math.min(t - 1, cur + 1); i++) items.push(i);
  if (cur < t - 2) items.push('…');
  items.push(t);
  return items;
});

// 获取产品列表数据（分页替换，不做追加）
const fetchProducts = () => {
  isLoading.value = true;
  noPermissionList(queryParams.value).then((response) => {
    isLoading.value = false;
    if (response && response.code === 200) {
      products.value = response.rows || [];
      total.value = response.total || 0;
    }
  }).catch(() => {
    isLoading.value = false;
  });
};

// 全部分类
const filterAll = () => {
  activeType.value = 0;
  activeLv1.value = 0;
  currentLv1Type.value = null;
  queryParams.value.typeId = undefined;
  goPage(1);
};

// 一级分类
const filterLv1 = (type) => {
  activeType.value = type.id;
  activeLv1.value = type.id;
  currentLv1Type.value = type;
  queryParams.value.typeId = type.id;
  goPage(1);
};

// 二级分类
const filterLv2 = (type) => {
  activeType.value = type.id;
  queryParams.value.typeId = type.id;
  goPage(1);
};

// 翻页
const goPage = (pageNum) => {
  if (pageNum < 1 || pageNum > totalPages.value) return;
  queryParams.value.pageNum = pageNum;
  fetchProducts();
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

// 获取类型树数据
const fetchTypeTree = () => {
  getTypeTree().then((response) => {
    if (response && response.code === 200) {
      treeData.value = response.data;
    }
  });
};

onMounted(() => {
  fetchTypeTree();
  fetchProducts();
});
</script>

<style scoped>
.product-page {
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--bg);
}

/* ===== 筛选栏 ===== */
.filter-bar {
  margin-top: -36px;
  position: relative;
  z-index: 5;
}

.filter-inner {
  background: #fff;
  border-radius: var(--r-lg);
  box-shadow: var(--shadow-md);
  padding: 20px 24px;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  border: 1px solid var(--line);
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.filter-group .label {
  font-size: 14px;
  color: var(--text-3);
}

/* ===== 搜索 ===== */
.search {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--bg);
  border: 1px solid var(--line);
  border-radius: 999px;
  padding: 8px 18px;
  min-width: 240px;
}

.search input {
  border: none;
  background: transparent;
  outline: none;
  font-size: 14px;
  flex: 1;
  color: var(--text-1);
}

.search button {
  border: none;
  background: var(--color-primary-500);
  color: #fff;
  border-radius: 999px;
  padding: 6px 16px;
  font-size: 13px;
  cursor: pointer;
  flex: none;
}

.search button:hover {
  background: var(--color-primary-600);
}

/* ===== 名录头 ===== */
.list-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin: 44px 0 22px;
}

.list-head .cn {
  font-size: 26px;
  font-weight: 700;
  color: var(--color-primary-900);
  letter-spacing: 1px;
}

.list-head .count {
  font-size: 14px;
  color: var(--text-3);
}

/* ===== 产品卡片网格 ===== */
.product-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.product-card {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  overflow: hidden;
  transition: all var(--normal);
  box-shadow: var(--shadow-sm);
  cursor: pointer;
  display: block;
}

.product-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-md);
  border-color: var(--color-primary-100);
}

.product-cover {
  height: 190px;
  position: relative;
  display: grid;
  place-items: center;
  background: #fff;
  border-bottom: 1px solid var(--line);
  overflow: hidden;
}

.product-cover img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 14px;
  box-sizing: border-box;
  transition: transform var(--normal);
}

.product-card:hover .product-cover img {
  transform: scale(1.04);
}

.product-body {
  padding: 16px 18px 18px;
}

.product-body b {
  font-size: 15px;
  color: var(--text-1);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  line-height: 1.5;
  min-height: 45px;
}

.product-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed var(--line);
}

.product-meta .price {
  font-size: 16px;
  font-weight: 700;
  color: var(--color-accent);
}

.product-meta .price:empty {
  display: none;
}

.product-meta .link {
  font-size: 13px;
  color: var(--color-primary-500);
  font-weight: 600;
}

.product-meta .link:hover {
  color: var(--color-primary-600);
}

/* ===== 加载与空状态 ===== */
.loading {
  text-align: center;
  padding: 60px 20px;
  font-size: 15px;
  color: var(--text-3);
}

.no-data {
  text-align: center;
  font-size: 18px;
  color: var(--text-3);
  padding: 80px 0;
}

/* ===== 响应式 ===== */
@media (max-width: 960px) {
  .product-grid {
    grid-template-columns: 1fr;
  }

  .search {
    margin-left: 0;
    width: 100%;
  }
}

@media (min-width: 961px) and (max-width: 1200px) {
  .product-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
